// Wave 3.51 — Edit Transaction Approval Workflow helpers.
//
// Provides a thin client-side facade over POST /api/edit-requests/.
//   * Super Admin → backend applies the edit immediately.
//   * Company Admin → backend creates a pending_sa request that the SA
//     can later approve / send back / reject from /edit-requests.
// Regular users cannot reach this surface (the backend returns 403 if
// they try).
//
// Wave 3.51 portal-wide rule (UPDATED): the edit window is **T+1 IST
// (calendar-based)** — a transaction created on IST day D can be
// edited on IST day D or D+1. The backend enforces this at both
// request-create AND approval-execute time; the UI uses the helpers
// below to hide the "Request Edit" button on stale transactions and
// to render the "Editable till <date>" badge.

import { api } from './api';

/**
 * Submit an edit-request. Returns the raw backend response so callers can
 * differentiate immediate apply (SA) vs pending workflow (CA).
 *
 * @param {object} opts
 * @param {'goldsmith_issue'|'goldsmith_return'|'vendor_purchase_return'|'packet_transfer'|'packet_recut'|'packet_send_to_broken'|'jewellery_receive'} opts.entity_type
 * @param {string} opts.entity_id
 * @param {string} [opts.entity_label]
 * @param {string} [opts.reason]
 * @param {object} opts.patch  Proposed changes (see backend executors)
 * @param {string} [opts.company_id]
 * @returns {Promise<{pending?:boolean, edited?:boolean, immediate?:boolean, request_id?:string, status?:string, summary?:object}>}
 */
export async function submitEdit(opts) {
  const payload = {
    entity_type: opts.entity_type,
    entity_id: opts.entity_id,
    entity_label: opts.entity_label || null,
    reason: opts.reason || '',
    patch: opts.patch || {},
    company_id: opts.company_id || null,
  };
  const { data } = await api.post('/edit-requests/', payload);
  return data;
}

// Human-friendly labels (used in lists + diff views).
export const EDIT_ENTITY_TYPE_LABELS = {
  goldsmith_issue: 'Goldsmith Issue',
  goldsmith_return: 'Goldsmith Return',
  vendor_purchase_return: 'Vendor Purchase Return',
  packet_transfer: 'Packet Transfer',
  packet_recut: 'Packet Re-cut',
  packet_send_to_broken: 'Send to Broken',
  jewellery_receive: 'Jewellery Receive',
};

// --------------------------------------------------------------------------
// T+1 IST (calendar-based) edit window helpers
// --------------------------------------------------------------------------
// A timestamp's IST calendar date is computed by shifting UTC by +05:30
// and then taking the date portion. The edit window covers two IST
// calendar days — the day of creation (D) AND the next day (D+1).

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function _istCalendarDateParts(date) {
  // Returns {y, m, d} for the IST calendar day of the given Date object.
  const shifted = new Date(date.getTime() + IST_OFFSET_MS);
  return {
    y: shifted.getUTCFullYear(),
    m: shifted.getUTCMonth(),
    d: shifted.getUTCDate(),
  };
}

function _istDateAsUTCMidnight(parts) {
  // Helper for computing the end-of-window date by adding +1 day.
  return new Date(Date.UTC(parts.y, parts.m, parts.d));
}

/**
 * Returns true if the given created-at timestamp is within the T+1 IST
 * window — i.e. the IST calendar day of `createdAt` is today (D) or
 * tomorrow (D+1) when measured against the user's "now" in IST.
 *
 * Equivalent: today_ist - created_ist ∈ {0, 1}.
 */
export function isWithinEditWindowT1(createdAt) {
  if (!createdAt) return false;
  try {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return false;
    const created = _istCalendarDateParts(d);
    const now = _istCalendarDateParts(new Date());
    const createdUTC = _istDateAsUTCMidnight(created);
    const nowUTC = _istDateAsUTCMidnight(now);
    const diffDays = Math.round(
      (nowUTC.getTime() - createdUTC.getTime()) / (24 * 60 * 60 * 1000),
    );
    return diffDays === 0 || diffDays === 1;
  } catch {
    return false;
  }
}

/**
 * Returns the LAST IST calendar date on which `createdAt` is still
 * editable under the T+1 policy, formatted via the provided formatter
 * (default: `DD MMM YYYY`).
 *
 * Returns null on bad input.
 */
export function editWindowEndDateT1(createdAt, { format = 'long' } = {}) {
  if (!createdAt) return null;
  try {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return null;
    const created = _istCalendarDateParts(d);
    // End of window = D + 1
    const end = new Date(Date.UTC(created.y, created.m, created.d + 1));
    const day = end.getUTCDate();
    const monthShort = end.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' });
    const monthLong = end.toLocaleString('en-IN', { month: 'long', timeZone: 'UTC' });
    const year = end.getUTCFullYear();
    if (format === 'short') return `${String(day).padStart(2, '0')}/${String(end.getUTCMonth() + 1).padStart(2, '0')}/${year}`;
    if (format === 'iso') return `${year}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (format === 'longest') return `${day} ${monthLong} ${year}`;
    // 'long' (default) → `DD MMM YYYY`
    return `${String(day).padStart(2, '0')} ${monthShort} ${year}`;
  } catch {
    return null;
  }
}

/**
 * @deprecated Use {@link isWithinEditWindowT1} instead. Kept temporarily so
 * existing imports (RequestEditDialog historically used this name) keep
 * working during the T+0 → T+1 transition.
 */
export function isSameIstDay(createdAt) {
  return isWithinEditWindowT1(createdAt);
}

// Returns the CSS classes used to highlight an edited cell or value.
// We use amber/orange per Wave 3.51 design choice. Components should
// also render a trailing asterisk inline.
export const EDITED_VALUE_CLASS = 'text-amber-600 font-medium';
export const EDITED_ROW_CLASS = 'bg-amber-50/40';
